import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {Gauge} from '@dropbox/dig-components/gauge';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  CheckmarkCircleLine,
  CheckmarkLine,
  ChevronDownLine,
  ChevronRightLine,
  DeleteLine,
  FlagLine,
  MetricsLine,
  OpenLine,
  WorkstreamsLine,
} from '@dropbox/dig-icons/assets';
import cx from 'classnames';
import {
  WorkstreamAuditOnTimeUpdates,
  WorkstreamExecSponsorInboxNeedsResolution,
  WorkstreamExecSponsorInboxOverdueMilestone,
  WorkstreamExecSponsorInboxReviewItem,
  WorkstreamExecSponsorInboxReviewItemChange,
  WorkstreamExecSponsorInboxWorkstream,
} from 'client';
import {getPlainTextFromRichTextValue} from 'components/DSYS/editor/utils/lexical';
import {EmployeeButton} from 'components/DSYS/EmployeeButton';
import {Eyebrow} from 'components/DSYS/Eyebrow';
import {Link} from 'components/DSYS/Link';
import {StatusChip} from 'components/DSYS/StatusChip';
import {Title} from 'components/DSYS/Title';
import {
  formatDateWithYear,
  formatShortDate,
  formatUtcDateWithYear,
  formatUtcShortDate,
} from 'helpers/dates';
import {formatMetricForecastPeriod} from 'helpers/metricForecast';
import {t} from 'i18next';
import {
  type ComponentProps,
  Fragment,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import {WorkstreamMetricStatusTag} from 'views/workstreams/detail/WorkstreamMetricStatus';
import {OutcomeStatusControl} from 'views/workstreams/detail/WorkstreamOutcomeStatus';
import {getWorkstreamDetailPath} from 'views/workstreams/routeUtils';
import {HealthScoreChecks, HealthScoreIcon} from 'views/workstreams/WorkstreamHealthScore';

import {
  logExecSponsorInboxInteraction,
  logExecSponsorInboxWorkstreamViewed,
} from './execSponsorInboxAnalytics';
import {useExecSponsorInboxDetail, useMarkReviewItemReviewed} from './useExecSponsorInbox';
import styles from './WorkstreamDetail.module.css';

export const normalizeStatus = (value?: string | null) =>
  value?.trim().toLocaleLowerCase().replace(/\s+/g, '_') || null;

// The item whose review is in flight, keyed like ReviewItemsSection's rows.
export const getPendingReviewItemKey = ({
  isPending,
  variables,
}: ReturnType<typeof useMarkReviewItemReviewed>) =>
  isPending && variables ? `${variables.item_type}-${variables.item_id}` : undefined;

const getReviewItemChange = (item: WorkstreamExecSponsorInboxReviewItem, fieldName: string) =>
  item.changes?.find((change) => change.field_name === fieldName);

const outcomeAddedTitleKeyByType = {
  deliverable: 'workstream_exec_sponsor_inbox_new_milestone',
  metric: 'workstream_exec_sponsor_inbox_new_metric',
  milestone: 'workstream_exec_sponsor_inbox_new_milestone',
} as const;

const outcomeDeletedTitleKeyByType = {
  deliverable: 'workstream_exec_sponsor_inbox_milestone_deleted',
  metric: 'workstream_exec_sponsor_inbox_metric_deleted',
  milestone: 'workstream_exec_sponsor_inbox_milestone_deleted',
} as const;

const outcomeTitleKeyByItemType = {
  outcome_added: outcomeAddedTitleKeyByType,
  outcome_deleted: outcomeDeletedTitleKeyByType,
} as const;

const outcomeIconByType = {
  deliverable: FlagLine,
  metric: MetricsLine,
  milestone: FlagLine,
} as const;

type OutcomeReviewItemType = keyof typeof outcomeTitleKeyByItemType;
type OutcomeType = keyof typeof outcomeIconByType;
const metricTypes = ['leading', 'lagging', 'other'] as const;
const REVIEW_ITEMS_PAGE_SIZE = 4;
type MetricType = (typeof metricTypes)[number];
const metricForecastPeriods = ['Q1', 'Q2', 'Q3', 'Q4', 'EOY'] as const;
const metricForecastChangedFields = ['actual', 'budget', 'target'] as const;
type MetricForecastValueField = (typeof metricForecastChangedFields)[number];
const outcomeChangedFields = [
  'baseline',
  'complete_date',
  'current',
  ...metricForecastChangedFields,
] as const;
type OutcomeChangedField = (typeof outcomeChangedFields)[number];

const outcomeChangedFieldLabelKey: Record<OutcomeChangedField, string> = {
  actual: 'actual',
  baseline: 'baseline',
  budget: 'budget',
  complete_date: 'eta',
  current: 'current',
  target: 'target',
};

const isOutcomeReviewItemType = (value?: string | null): value is OutcomeReviewItemType =>
  value === 'outcome_added' || value === 'outcome_deleted';

const isOutcomeType = (value?: string | null): value is OutcomeType =>
  value === 'deliverable' || value === 'metric' || value === 'milestone';

const isMetricType = (value?: string | null): value is MetricType =>
  metricTypes.some((metricType) => metricType === value);

const isOutcomeChangedField = (value: string): value is OutcomeChangedField =>
  outcomeChangedFields.some((field) => field === value);

const isSupportedOutcomeChange = (
  change: WorkstreamExecSponsorInboxReviewItemChange
): change is WorkstreamExecSponsorInboxReviewItemChange & {field_name: OutcomeChangedField} =>
  isOutcomeChangedField(change.field_name);

const getOutcomeChangeForecastPeriod = (
  item: WorkstreamExecSponsorInboxReviewItem,
  change: WorkstreamExecSponsorInboxReviewItemChange
) => change.forecast_period ?? item.forecast_period;

const isMetricForecastChange = (
  item: WorkstreamExecSponsorInboxReviewItem,
  change: WorkstreamExecSponsorInboxReviewItemChange
) =>
  item.item_type === 'outcome_changed' &&
  item.outcome_type === 'metric' &&
  Boolean(getOutcomeChangeForecastPeriod(item, change));

const isSupportedOutcomeChangeForItem = (
  item: WorkstreamExecSponsorInboxReviewItem,
  change: WorkstreamExecSponsorInboxReviewItemChange
): change is WorkstreamExecSponsorInboxReviewItemChange & {field_name: OutcomeChangedField} => {
  if (!isSupportedOutcomeChange(change)) {
    return false;
  }
  if (!change.previous_value?.trim() && !change.new_value?.trim()) {
    return false;
  }
  if (metricForecastChangedFields.some((field) => field === change.field_name)) {
    return isMetricForecastChange(item, change);
  }
  return true;
};

const getSupportedOutcomeChanges = (item: WorkstreamExecSponsorInboxReviewItem) =>
  (item.changes ?? []).filter((change) => isSupportedOutcomeChangeForItem(item, change));

const isOutcomeChangedReviewItem = (item: WorkstreamExecSponsorInboxReviewItem) =>
  item.item_type === 'outcome_changed' &&
  isOutcomeType(item.outcome_type) &&
  getSupportedOutcomeChanges(item).length > 0;

const isCompletedMilestoneReviewItem = (item: WorkstreamExecSponsorInboxReviewItem) =>
  item.item_type === 'outcome_status_changed' &&
  (item.outcome_type === 'milestone' || item.outcome_type === 'deliverable') &&
  normalizeStatus(getReviewItemChange(item, 'status')?.new_value) === 'completed';

const isMilestoneStatusChangedReviewItem = (item: WorkstreamExecSponsorInboxReviewItem) =>
  item.item_type === 'outcome_status_changed' &&
  (item.outcome_type === 'milestone' || item.outcome_type === 'deliverable');

const isMetricStatusChangedReviewItem = (item: WorkstreamExecSponsorInboxReviewItem) =>
  item.item_type === 'outcome_status_changed' && item.outcome_type === 'metric';

export const isSupportedReviewItem = (item: WorkstreamExecSponsorInboxReviewItem) =>
  item.item_type === 'status_update' ||
  (isOutcomeReviewItemType(item.item_type) && isOutcomeType(item.outcome_type)) ||
  isOutcomeChangedReviewItem(item) ||
  isMilestoneStatusChangedReviewItem(item) ||
  isMetricStatusChangedReviewItem(item);

export const isReviewedReviewItem = (item: WorkstreamExecSponsorInboxReviewItem) =>
  item.is_reviewed ?? Boolean(item.reviewed_at);

const getReviewItems = (
  items: WorkstreamExecSponsorInboxReviewItem[] | undefined,
  isReviewed: boolean
) =>
  (items ?? []).filter(
    (item) => isSupportedReviewItem(item) && isReviewedReviewItem(item) === isReviewed
  );

const OnTimeUpdatesGauge = ({onTimeUpdates}: {onTimeUpdates: WorkstreamAuditOnTimeUpdates}) => {
  const submittedCount = onTimeUpdates.submitted_count;
  const cycleCount = onTimeUpdates.cycle_count;
  const value = Math.min(Math.max((submittedCount / cycleCount) * 100, 0), 100);
  const valueText = t('workstream_audit_on_time_updates_value', {
    submittedCount,
    cycleCount,
  }).toString();
  return (
    <Gauge
      value={value}
      size="xsmall"
      aria-label={t('workstream_audit_on_time_updates').toString()}
      aria-valuetext={valueText}
    >
      <Gauge.Content>
        <Gauge.Subtext className={styles.onTimeGaugeSubtext}>
          {`${submittedCount}/${cycleCount}`}
        </Gauge.Subtext>
      </Gauge.Content>
    </Gauge>
  );
};

const SummaryGridItem = ({label, children}: {label: ReactNode; children: ReactNode}) => (
  <Stack gap="8">
    <Text color="subtle" size="small">
      {label}
    </Text>
    {children}
  </Stack>
);

const DetailSectionHeader = ({
  count,
  leadingAccessory,
  title,
  titleId,
}: {
  count: number;
  leadingAccessory?: ReactNode;
  title: ReactNode;
  titleId?: string;
}) => (
  <Box className={styles.detailSectionHeader}>
    {leadingAccessory}
    <Box id={titleId}>
      <Eyebrow size="small">{title}</Eyebrow>
    </Box>
    <Box marginLeft="6">
      <Text color="subtle" size="small">
        {count}
      </Text>
    </Box>
  </Box>
);

const CollapsibleDetailSection = ({
  children,
  contentClassName,
  count,
  onExpandedChange,
  sectionClassName,
  sectionId,
  title,
}: {
  children: ReactNode;
  contentClassName?: string;
  count: number;
  onExpandedChange?: (isExpanded: boolean) => void;
  sectionClassName: string;
  sectionId: string;
  title: ReactNode;
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const contentId = `${sectionId}-content`;
  const titleId = `${sectionId}-title`;
  const toggleExpanded = () => {
    const nextExpanded = !isExpanded;
    onExpandedChange?.(nextExpanded);
    setIsExpanded(nextExpanded);
  };

  return (
    <Box className={sectionClassName}>
      <DetailSectionHeader
        title={title}
        titleId={titleId}
        count={count}
        leadingAccessory={
          <IconButton
            type="button"
            variant="borderless"
            size="small"
            aria-labelledby={titleId}
            aria-expanded={isExpanded}
            aria-controls={contentId}
            style={{marginLeft: -4}}
            onClick={toggleExpanded}
          >
            <UIIcon
              src={isExpanded ? ChevronDownLine : ChevronRightLine}
              size="small"
              className={atoms({color: 'Text Subtle'})}
            />
          </IconButton>
        }
      />
      {isExpanded && (
        <Box id={contentId} className={contentClassName}>
          {children}
        </Box>
      )}
    </Box>
  );
};

const ReviewItemHeader = ({
  icon,
  title,
}: {
  icon: ComponentProps<typeof UIIcon>['src'];
  title: ReactNode;
}) => (
  <Box className={styles.reviewItemHeader}>
    <Box className={styles.reviewItemIcon}>
      <UIIcon src={icon} size="medium" aria-hidden="true" />
    </Box>
    <Text isBold>{title}</Text>
  </Box>
);

const OverdueMilestoneItem = ({
  milestone,
}: {
  milestone: WorkstreamExecSponsorInboxOverdueMilestone;
}) => {
  const dueDate = formatShortDate(milestone.due_date) || milestone.due_date;
  const dueDateText = milestone.owner?.name
    ? t('workstream_exec_sponsor_inbox_overdue_milestone_due_owner', {
        dueDate,
        ownerName: milestone.owner.name,
      })
    : t('workstream_audit_update_cycle_due_date', {dueDate});
  return (
    <Box
      className={cx(styles.detailItemCard, styles.reviewItemPadding, styles.overdueMilestoneCard)}
    >
      <UIIcon
        src={FlagLine}
        size="medium"
        aria-hidden="true"
        className={styles.overdueMilestoneIcon}
      />
      <Stack gap="8">
        <Text>{t('workstream_exec_sponsor_inbox_milestone_overdue')}</Text>
        <Stack gap="4">
          <Text isBold>{getPlainTextFromRichTextValue(milestone.title)}</Text>
          <Box>
            <Text size="small" color="subtle">
              {dueDateText}
            </Text>
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};

const ReviewItemMetadata = ({
  author,
  timestamp,
}: {
  author?: WorkstreamExecSponsorInboxReviewItem['author'];
  timestamp?: string;
}) => {
  if (!timestamp && !author) {
    return null;
  }

  return (
    <Box className={styles.reviewItemMetadata}>
      {!!timestamp && (
        <Text color="subtle" size="small">
          {timestamp}
        </Text>
      )}
      {timestamp && author && (
        <Text color="subtle" size="small" aria-hidden="true">
          ·
        </Text>
      )}
      {author && (
        <Box className={styles.reviewItemAuthor}>
          <EmployeeButton
            employee={author}
            avatarSize="xsmall"
            display="inline-block"
            size="small"
            color="subtle"
          />
        </Box>
      )}
    </Box>
  );
};

const ReviewItemCard = ({
  author,
  children,
  icon,
  reviewedAt,
  reviewerUserName,
  timestamp,
  title,
}: {
  author?: WorkstreamExecSponsorInboxReviewItem['author'];
  children: ReactNode;
  icon: ComponentProps<typeof UIIcon>['src'];
  reviewedAt?: string | null;
  reviewerUserName?: string | null;
  timestamp?: string;
  title: ReactNode;
}) => {
  const reviewedDate = formatUtcDateWithYear(reviewedAt);
  return (
    <Box className={cx(styles.detailItemCard, styles.reviewItemPadding, styles.reviewItemCard)}>
      <ReviewItemHeader icon={icon} title={title} />
      {children}
      <ReviewItemMetadata author={author} timestamp={timestamp} />
      {!!reviewerUserName && !!reviewedDate && (
        <Text tagName="div" color="subtle" size="small" className={styles.reviewItemReviewMetadata}>
          {t('workstream_exec_sponsor_inbox_reviewed_by', {
            reviewerName: reviewerUserName,
            reviewedDate,
          })}
        </Text>
      )}
    </Box>
  );
};

const ReviewItemOutcomeTitle = ({
  title,
  color,
  className,
}: {
  title?: string | null;
  color?: ComponentProps<typeof Text>['color'];
  className?: string;
}) =>
  title ? (
    <Text tagName="div" color={color} className={className} isBold>
      {title}
    </Text>
  ) : null;

const ReviewItemOutcomeReason = ({reason}: {reason?: string | null}) =>
  reason ? (
    <Text tagName="div" color="subtle" size="small" className={styles.reviewItemOutcomeReason}>
      {t('workstream_exec_sponsor_inbox_outcome_change_reason', {reason})}
    </Text>
  ) : null;

const ReviewItemOutcomeBody = ({
  children,
  reason,
}: {
  children: ReactNode;
  reason?: string | null;
}) => (
  <Box>
    {children}
    <ReviewItemOutcomeReason reason={reason} />
  </Box>
);

const NewMetricForecastTable = ({item}: {item: WorkstreamExecSponsorInboxReviewItem}) => {
  const forecasts = item.metric_forecasts ?? [];
  const forecastsByPeriod = new Map(
    forecasts.map((forecast) => [formatMetricForecastPeriod(forecast.period, false), forecast])
  );
  const metricType = isMetricType(item.metric_type) ? t(item.metric_type) : null;
  const forecastValueFields = metricForecastChangedFields.filter((field) =>
    forecasts.some((forecast) => Boolean(forecast[field]?.trim()))
  );
  const forecastRows: Array<MetricForecastValueField | null> =
    forecastValueFields.length > 0 ? forecastValueFields : [null];
  const summaryColumns = [
    {label: t('dri'), value: item.metric_dri?.name ?? '—'},
    {label: t('type'), value: metricType ?? '—'},
    {label: t('baseline'), value: item.baseline?.trim() || '—'},
    {label: t('current'), value: item.current?.trim() || '—'},
  ];
  const headerLabels = [
    ...summaryColumns.map(({label}) => label),
    t('values'),
    ...metricForecastPeriods,
  ];
  const getForecastValue = (period: string, field: MetricForecastValueField) =>
    forecastsByPeriod.get(period)?.[field]?.trim() || '—';

  return (
    <Box className={styles.newMetricForecastTable}>
      <Box className={styles.newMetricForecastTableGrid}>
        {headerLabels.map((label) => (
          <Text
            key={`header-${label}`}
            className={styles.newMetricForecastHeader}
            color="subtle"
            size="small"
          >
            {label}
          </Text>
        ))}
        {summaryColumns.map(({label, value}) => (
          <Text
            key={`value-${label}`}
            className={styles.newMetricForecastSummaryCell}
            color={value === '—' ? 'subtle' : undefined}
            size="small"
            style={{gridRow: `span ${forecastRows.length}`}}
          >
            {value}
          </Text>
        ))}
        {forecastRows.map((field, rowIndex) => (
          <Fragment key={field ?? `empty-${rowIndex}`}>
            <Text color="subtle" size="small">
              {field ? t(field) : '—'}
            </Text>
            {metricForecastPeriods.map((period) => {
              const value = field ? getForecastValue(period, field) : '—';
              return (
                <Text
                  key={`${field ?? 'empty'}-${period}`}
                  color={value === '—' ? 'subtle' : undefined}
                  size="small"
                >
                  {value}
                </Text>
              );
            })}
          </Fragment>
        ))}
      </Box>
    </Box>
  );
};

const StatusUpdateReviewItem = ({item}: {item: WorkstreamExecSponsorInboxReviewItem}) => {
  const status = normalizeStatus(getReviewItemChange(item, 'status')?.new_value);
  return (
    <ReviewItemCard
      author={item.author}
      icon={WorkstreamsLine}
      reviewedAt={item.reviewed_at}
      reviewerUserName={item.reviewer_user_name}
      timestamp={formatUtcDateWithYear(item.item_created_at)}
      title={t('workstream_exec_sponsor_inbox_new_status_update')}
    >
      <Stack gap="8">
        {!!status && <StatusChip status={status} size="small" />}
        <Text tagName="div">{getPlainTextFromRichTextValue(item.comment)}</Text>
      </Stack>
    </ReviewItemCard>
  );
};

const OutcomeReviewItem = ({item}: {item: WorkstreamExecSponsorInboxReviewItem}) => {
  const outcomeType = item.outcome_type;
  if (!isOutcomeReviewItemType(item.item_type) || !isOutcomeType(outcomeType)) {
    return null;
  }

  const titleKey = outcomeTitleKeyByItemType[item.item_type][outcomeType];
  const isDeletedOutcome = item.item_type === 'outcome_deleted';
  const icon = isDeletedOutcome ? DeleteLine : outcomeIconByType[outcomeType];
  const title = getPlainTextFromRichTextValue(item.title);
  const dueDate = formatDateWithYear(item.due_date);
  const createdAt = formatUtcDateWithYear(item.item_created_at);
  const isNewMetric = item.item_type === 'outcome_added' && outcomeType === 'metric';
  return (
    <ReviewItemCard
      author={item.author}
      icon={icon}
      reviewedAt={item.reviewed_at}
      reviewerUserName={item.reviewer_user_name}
      timestamp={createdAt}
      title={t(titleKey)}
    >
      <ReviewItemOutcomeBody reason={item.reason}>
        <Stack gap="2">
          <ReviewItemOutcomeTitle
            title={title}
            color={isDeletedOutcome ? 'subtle' : undefined}
            className={isDeletedOutcome ? styles.deletedOutcomeName : undefined}
          />
          {isNewMetric && <NewMetricForecastTable item={item} />}
          {!isDeletedOutcome && !!dueDate && (
            <Text tagName="div" color="subtle" size="small">
              {t('workstream_audit_update_cycle_due_date', {dueDate})}
            </Text>
          )}
        </Stack>
      </ReviewItemOutcomeBody>
    </ReviewItemCard>
  );
};

const OutcomeChangedReviewItem = ({item}: {item: WorkstreamExecSponsorInboxReviewItem}) => {
  const outcomeType = item.outcome_type;
  if (!isOutcomeType(outcomeType)) {
    return null;
  }

  const changes = getSupportedOutcomeChanges(item);
  const title = getPlainTextFromRichTextValue(item.title);
  const isMetric = outcomeType === 'metric';
  const formatChangeValue = (fieldName: string, value: string) => {
    return fieldName === 'complete_date' ? formatDateWithYear(value) : value;
  };
  const getChangeLabel = (
    change: WorkstreamExecSponsorInboxReviewItemChange & {field_name: OutcomeChangedField}
  ) => {
    const fieldLabel = t(outcomeChangedFieldLabelKey[change.field_name]).toString();
    const forecastPeriod = formatMetricForecastPeriod(
      getOutcomeChangeForecastPeriod(item, change),
      false
    );
    return forecastPeriod ? `${forecastPeriod} ${fieldLabel}` : fieldLabel;
  };
  return (
    <ReviewItemCard
      author={item.author}
      icon={isMetric ? MetricsLine : FlagLine}
      reviewedAt={item.reviewed_at}
      reviewerUserName={item.reviewer_user_name}
      timestamp={formatUtcDateWithYear(item.item_created_at)}
      title={t(
        isMetric
          ? 'workstream_exec_sponsor_inbox_metric_changed'
          : 'workstream_exec_sponsor_inbox_milestone_changed'
      )}
    >
      <ReviewItemOutcomeBody reason={item.reason}>
        <Stack gap="8">
          <ReviewItemOutcomeTitle title={title} />
          <Stack gap="8">
            {changes.map((change) => (
              <Box
                key={`${getOutcomeChangeForecastPeriod(item, change) ?? ''}-${change.field_name}`}
                className={styles.outcomeChangeRow}
              >
                <Text color="subtle" size="small">
                  {getChangeLabel(change)}
                </Text>
                <Stack gap="2">
                  {!!change.previous_value && (
                    <Text
                      tagName="div"
                      color="subtle"
                      className={styles.outcomeChangePreviousValue}
                    >
                      {formatChangeValue(change.field_name, change.previous_value)}
                    </Text>
                  )}
                  {!!change.new_value && (
                    <Text tagName="div">
                      {formatChangeValue(change.field_name, change.new_value)}
                    </Text>
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Stack>
      </ReviewItemOutcomeBody>
    </ReviewItemCard>
  );
};

const MetricStatusChangedReviewItem = ({item}: {item: WorkstreamExecSponsorInboxReviewItem}) => {
  const title = getPlainTextFromRichTextValue(item.title);
  const createdAt = formatUtcDateWithYear(item.item_created_at);
  const statusChange = getReviewItemChange(item, 'status');
  return (
    <ReviewItemCard
      author={item.author}
      icon={MetricsLine}
      reviewedAt={item.reviewed_at}
      reviewerUserName={item.reviewer_user_name}
      timestamp={createdAt}
      title={t('workstream_exec_sponsor_inbox_metric_status_changed')}
    >
      <ReviewItemOutcomeBody reason={item.reason}>
        <Stack gap="8">
          <ReviewItemOutcomeTitle title={title} />
          {statusChange && (
            <Box className={styles.metricStatusChangeValues}>
              <WorkstreamMetricStatusTag status={statusChange.previous_value} size="small" />
              <UIIcon src={ChevronRightLine} size="small" aria-hidden="true" />
              <WorkstreamMetricStatusTag status={statusChange.new_value} size="small" />
            </Box>
          )}
        </Stack>
      </ReviewItemOutcomeBody>
    </ReviewItemCard>
  );
};

const MilestoneStatusChangedReviewItem = ({item}: {item: WorkstreamExecSponsorInboxReviewItem}) => {
  const title = getPlainTextFromRichTextValue(item.title);
  const createdAt = formatUtcDateWithYear(item.item_created_at);
  const statusChange = getReviewItemChange(item, 'status');
  return (
    <ReviewItemCard
      author={item.author}
      icon={FlagLine}
      reviewedAt={item.reviewed_at}
      reviewerUserName={item.reviewer_user_name}
      timestamp={createdAt}
      title={t('workstream_exec_sponsor_inbox_milestone_status_changed')}
    >
      <ReviewItemOutcomeBody reason={item.reason}>
        <Stack gap="8">
          <ReviewItemOutcomeTitle title={title} />
          {statusChange && (
            <Box className={styles.metricStatusChangeValues}>
              <OutcomeStatusControl status={statusChange.previous_value} size="small" />
              <UIIcon src={ChevronRightLine} size="small" aria-hidden="true" />
              <OutcomeStatusControl status={statusChange.new_value} size="small" />
            </Box>
          )}
        </Stack>
      </ReviewItemOutcomeBody>
    </ReviewItemCard>
  );
};

const CompletedMilestoneReviewItem = ({item}: {item: WorkstreamExecSponsorInboxReviewItem}) => {
  const title = getPlainTextFromRichTextValue(item.title);
  const createdAt = formatUtcDateWithYear(item.item_created_at);
  const completedDate = formatUtcDateWithYear(item.completed_at);
  const completionUrl = item.url?.trim();
  const hasSafeCompletionUrl = /^https?:\/\//i.test(completionUrl ?? '');

  return (
    <ReviewItemCard
      author={item.author}
      icon={CheckmarkCircleLine}
      reviewedAt={item.reviewed_at}
      reviewerUserName={item.reviewer_user_name}
      timestamp={createdAt}
      title={t('workstream_exec_sponsor_inbox_milestone_completed')}
    >
      <ReviewItemOutcomeBody reason={item.reason}>
        <Stack gap="8">
          <Stack gap="2">
            <ReviewItemOutcomeTitle title={title} />
            {!!completedDate && (
              <Text tagName="div" color="subtle" size="small">
                {t('workstream_exec_sponsor_inbox_milestone_completed_date', {completedDate})}
              </Text>
            )}
          </Stack>
          {hasSafeCompletionUrl && completionUrl && (
            <Link
              to={completionUrl}
              hasNoUnderline
              onClick={() =>
                logExecSponsorInboxInteraction({
                  action: 'launch_doc_opened',
                  itemType: item.item_type,
                  outcomeType: item.outcome_type,
                })
              }
            >
              <Box className={styles.completedMilestoneLink}>
                <UIIcon src={OpenLine} size="small" aria-hidden="true" />
                <Text size="small" className={styles.completedMilestoneLinkText}>
                  {t('workstream_exec_sponsor_inbox_view_launch_doc')}
                </Text>
              </Box>
            </Link>
          )}
        </Stack>
      </ReviewItemOutcomeBody>
    </ReviewItemCard>
  );
};

const ReviewItemCheck = ({
  isReviewed,
  isReviewPending = false,
  onReview,
}: {
  isReviewed: boolean;
  isReviewPending?: boolean;
  onReview?: () => void;
}) => {
  if (!isReviewed) {
    return (
      <IconButton
        data-testid="unread-review-item-check"
        className={styles.reviewItemCheck}
        aria-label={t('workstream_exec_sponsor_inbox_mark_reviewed').toString()}
        variant="primary"
        size="small"
        disabled={isReviewPending}
        onClick={onReview}
      >
        <UIIcon
          src={CheckmarkLine}
          size="small"
          className={atoms({color: 'Primary On Base'})}
          aria-hidden="true"
        />
      </IconButton>
    );
  }

  return (
    <Box
      data-testid="reviewed-review-item-check"
      className={cx(styles.reviewItemCheck, styles.reviewedReviewItemCheck)}
      alignItems="center"
      backgroundColor="Success Surface"
      borderRadius="Circular"
      color="Success Base"
      display="flex"
      justifyContent="center"
    >
      <UIIcon src={CheckmarkLine} size="small" aria-hidden="true" />
    </Box>
  );
};

const ReviewItem = ({
  item,
  isReviewed,
  isReviewPending,
  onReview,
}: {
  item: WorkstreamExecSponsorInboxReviewItem;
  isReviewed: boolean;
  isReviewPending?: boolean;
  onReview?: (item: WorkstreamExecSponsorInboxReviewItem) => void;
}) => {
  let content: ReactNode;
  if (isCompletedMilestoneReviewItem(item)) {
    content = <CompletedMilestoneReviewItem item={item} />;
  } else if (isMilestoneStatusChangedReviewItem(item)) {
    content = <MilestoneStatusChangedReviewItem item={item} />;
  } else if (isMetricStatusChangedReviewItem(item)) {
    content = <MetricStatusChangedReviewItem item={item} />;
  } else if (isOutcomeChangedReviewItem(item)) {
    content = <OutcomeChangedReviewItem item={item} />;
  } else if (isOutcomeReviewItemType(item.item_type)) {
    content = <OutcomeReviewItem item={item} />;
  } else {
    content = <StatusUpdateReviewItem item={item} />;
  }

  if (!isReviewed) {
    return (
      <Box className={styles.reviewItem}>
        {content}
        <ReviewItemCheck
          isReviewed={false}
          isReviewPending={isReviewPending}
          onReview={() => onReview?.(item)}
        />
      </Box>
    );
  }

  return (
    <Box className={styles.reviewItem}>
      <Box className={styles.reviewedReviewItemContent}>{content}</Box>
      <ReviewItemCheck isReviewed />
    </Box>
  );
};

export const ReviewItemsSection = ({
  items,
  isReviewed,
  pendingReviewItemKey,
  onReview,
  showHeader = true,
}: {
  items?: WorkstreamExecSponsorInboxReviewItem[];
  isReviewed: boolean;
  pendingReviewItemKey?: string;
  onReview?: (item: WorkstreamExecSponsorInboxReviewItem) => void;
  // Off when the caller labels the section itself (e.g. a fold toggle).
  showHeader?: boolean;
}) => {
  const [visibleItemCount, setVisibleItemCount] = useState(REVIEW_ITEMS_PAGE_SIZE);
  const reviewItems = getReviewItems(items, isReviewed);
  const visibleReviewItems = reviewItems.slice(0, visibleItemCount);
  const hasMoreReviewItems = visibleItemCount < reviewItems.length;
  const handleViewMore = () => {
    const nextVisibleItemCount = Math.min(
      visibleItemCount + REVIEW_ITEMS_PAGE_SIZE,
      reviewItems.length
    );
    logExecSponsorInboxInteraction({
      action: 'view_more',
      section: isReviewed ? 'reviewed' : 'unread',
      visibleItemCount: nextVisibleItemCount,
      totalItemCount: reviewItems.length,
    });
    setVisibleItemCount(nextVisibleItemCount);
  };
  if (!items) {
    return null;
  }
  if (reviewItems.length === 0) {
    return isReviewed ? null : (
      <Box className={styles.reviewItemsEmptyState}>
        <Text color="subtle">{t('workstream_exec_sponsor_inbox_nothing_to_review')}</Text>
      </Box>
    );
  }

  return (
    <Box className={showHeader ? styles.reviewItemsSection : styles.reviewItemsContent}>
      {showHeader && (
        <DetailSectionHeader
          title={t(
            isReviewed
              ? 'workstream_exec_sponsor_inbox_reviewed'
              : 'workstream_exec_sponsor_inbox_unread'
          )}
          count={reviewItems.length}
        />
      )}
      <Stack gap="12">
        {visibleReviewItems.map((item) => {
          const reviewItemKey = `${item.item_type}-${item.item_id}`;
          return (
            <ReviewItem
              key={reviewItemKey}
              item={item}
              isReviewed={isReviewed}
              isReviewPending={pendingReviewItemKey === reviewItemKey}
              onReview={onReview}
            />
          );
        })}
      </Stack>
      {hasMoreReviewItems && (
        <Box display="flex" justifyContent="center">
          <Button variant="transparent" size="small" onClick={handleViewMore}>
            {t('view_more')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export const NeedsResolutionSection = ({
  needsResolution,
  workstreamId,
}: {
  needsResolution?: WorkstreamExecSponsorInboxNeedsResolution;
  workstreamId: number;
}) => {
  const [isReadinessExpanded, setIsReadinessExpanded] = useState(true);
  const readiness = needsResolution?.readiness;
  const overdueMilestones = needsResolution?.overdue_milestones ?? [];
  const resolutionItemCount = needsResolution?.count ?? 0;
  const failedReadinessChecks = (readiness?.failed_check_ids ?? []).map((id) => ({
    id,
    passed: false,
  }));
  const failedChecksId = `exec-sponsor-inbox-readiness-checks-${workstreamId}`;
  const toggleReadinessExpanded = () => {
    const nextExpanded = !isReadinessExpanded;
    logExecSponsorInboxInteraction({
      action: 'section_toggled',
      section: 'readiness',
      isExpanded: nextExpanded,
    });
    setIsReadinessExpanded(nextExpanded);
  };
  const handleReadinessKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    toggleReadinessExpanded();
  };

  if (!needsResolution || resolutionItemCount <= 0) {
    return null;
  }

  return (
    <CollapsibleDetailSection
      sectionClassName={styles.needsResolutionSection}
      contentClassName={styles.needsResolutionItems}
      sectionId={`exec-sponsor-inbox-needs-resolution-${workstreamId}`}
      title={t('workstream_exec_sponsor_inbox_needs_resolution')}
      count={resolutionItemCount}
      onExpandedChange={(isExpanded) =>
        logExecSponsorInboxInteraction({
          action: 'section_toggled',
          section: 'needs_resolution',
          isExpanded,
        })
      }
    >
      {readiness && (
        <Box className={styles.detailItemCard}>
          <Box
            role="button"
            tabIndex={0}
            className={cx(styles.reviewItemPadding, styles.needsResolutionItemToggle)}
            aria-expanded={isReadinessExpanded}
            aria-controls={failedReadinessChecks.length > 0 ? failedChecksId : undefined}
            onClick={toggleReadinessExpanded}
            onKeyDown={handleReadinessKeyDown}
          >
            <UIIcon
              src={isReadinessExpanded ? ChevronDownLine : ChevronRightLine}
              size="small"
              aria-hidden="true"
            />
            <HealthScoreIcon
              score={readiness.score ?? undefined}
              iconSize={24}
              isBold={false}
              showText
            />
          </Box>

          {isReadinessExpanded && failedReadinessChecks.length > 0 && (
            <Box className={styles.readinessResolutionChecks} id={failedChecksId}>
              <HealthScoreChecks
                entityId={workstreamId}
                checks={failedReadinessChecks}
                iconGap={4}
                textSize="medium"
              />
            </Box>
          )}
        </Box>
      )}

      {overdueMilestones.map((milestone) => (
        <OverdueMilestoneItem key={milestone.id} milestone={milestone} />
      ))}
    </CollapsibleDetailSection>
  );
};

export const WorkstreamDetail = ({
  workstream,
}: {
  workstream: WorkstreamExecSponsorInboxWorkstream;
}) => {
  const workstreamId = workstream.id;
  const lastLoggedSelectedWorkstreamIdRef = useRef<number | null>(null);
  const {data, isLoading} = useExecSponsorInboxDetail(workstreamId);
  const reviewMutation = useMarkReviewItemReviewed(workstreamId);
  const pendingReviewItemKey = getPendingReviewItemKey(reviewMutation);

  const detail = data ?? workstream;
  const descriptionText = getPlainTextFromRichTextValue(detail.description);
  const status = normalizeStatus(detail.latest_status_update);
  const updatedDate = formatUtcShortDate(detail.latest_status_update_at);
  const onTimeUpdates = data?.on_time_updates;
  const reviewItems = data ? (data.review_items ?? []) : undefined;
  const reviewedReviewItemCount = getReviewItems(reviewItems, true).length;

  useEffect(() => {
    if (!data || lastLoggedSelectedWorkstreamIdRef.current === data.id) {
      return;
    }

    const supportedReviewItems = (data.review_items ?? []).filter(isSupportedReviewItem);
    const unreadReviewItems = supportedReviewItems.filter((item) => !isReviewedReviewItem(item));
    lastLoggedSelectedWorkstreamIdRef.current = data.id;

    logExecSponsorInboxWorkstreamViewed({
      readinessScore: data.readiness?.score,
      needsResolutionCount: data.needs_resolution?.count ?? 0,
      overdueMilestoneCount: data.needs_resolution?.overdue_milestones?.length ?? 0,
      unreadReviewItemTypes: Array.from(
        new Set(unreadReviewItems.map((item) => item.item_type))
      ).sort(),
      unreadReviewItemCount: unreadReviewItems.length,
      allReviewItemCount: supportedReviewItems.length,
    });
  }, [data]);

  return (
    <Box className={styles.detail}>
      <Stack gap="16">
        <Stack gap="12">
          <Box display="flex" alignItems="center" columnGap="8">
            <Title size={24}>{detail.name}</Title>
            <Link
              to={getWorkstreamDetailPath({id: workstreamId})}
              aria-label={t('view_workstream').toString()}
              openInNewTab
              hasNoUnderline
              onClick={() => logExecSponsorInboxInteraction({action: 'workstream_opened'})}
            >
              <UIIcon src={OpenLine} size="small" className={atoms({color: 'Text Subtle'})} />
            </Link>
          </Box>
          {detail.dri && (
            <EmployeeButton employee={detail.dri} avatarSize="small" display="inline-block" />
          )}
        </Stack>

        {!!descriptionText && <Text tagName="div">{descriptionText}</Text>}

        {isLoading && !data ? (
          <Box className={styles.summaryGrid}>
            <Skeleton.Text width={120} />
            <Skeleton.Text width={120} />
            <Skeleton.Text width={120} />
          </Box>
        ) : (
          <Box className={styles.summaryGrid}>
            {!!status && (
              <SummaryGridItem label={t('status')}>
                <StatusChip status={status} size="small" />
                {!!updatedDate && (
                  <Box marginTop="6">
                    <Text size="small" color="subtle">
                      {t('status_updated_date', {date: updatedDate})}
                    </Text>
                  </Box>
                )}
              </SummaryGridItem>
            )}

            {!!data?.readiness?.score && (
              <SummaryGridItem label={t('health_readiness')}>
                <HealthScoreIcon
                  score={data.readiness.score}
                  iconSize={24}
                  textSize="small"
                  showText
                />
              </SummaryGridItem>
            )}

            {onTimeUpdates && onTimeUpdates.cycle_count > 0 && (
              <SummaryGridItem label={t('workstream_audit_on_time_updates')}>
                <OnTimeUpdatesGauge onTimeUpdates={onTimeUpdates} />
              </SummaryGridItem>
            )}
          </Box>
        )}

        <NeedsResolutionSection
          key={workstreamId}
          workstreamId={workstreamId}
          needsResolution={data?.needs_resolution}
        />

        <ReviewItemsSection
          key={`${workstreamId}-unread`}
          items={reviewItems}
          isReviewed={false}
          pendingReviewItemKey={pendingReviewItemKey}
          onReview={(item) => reviewMutation.mutate(item)}
        />
        {reviewedReviewItemCount > 0 && (
          <CollapsibleDetailSection
            key={`${workstreamId}-reviewed`}
            sectionClassName={styles.reviewItemsSection}
            sectionId={`exec-sponsor-inbox-reviewed-${workstreamId}`}
            title={t('workstream_exec_sponsor_inbox_reviewed')}
            count={reviewedReviewItemCount}
            onExpandedChange={(isExpanded) =>
              logExecSponsorInboxInteraction({
                action: 'section_toggled',
                section: 'reviewed',
                isExpanded,
              })
            }
          >
            <ReviewItemsSection items={reviewItems} isReviewed showHeader={false} />
          </CollapsibleDetailSection>
        )}
      </Stack>
    </Box>
  );
};
